import { create } from 'zustand'
import { api } from '../api/client.ts'
import { sendCallSignal } from '../realtime/socket.ts'
import { startRinging } from '../lib/ring.ts'
import type { Call } from '../api/types.ts'
import { forRecording, releaseRecording } from '../lib/audio-session.ts'

/**
 * ЗВОНОК
 *
 * Разговор идёт напрямую между двумя браузерами. Сервер только
 * знакомит собеседников: передаёт описание звука и сетевые адреса.
 * Сам звук через сервер не проходит — значит, его нечем записать,
 * и это не обещание, а свойство устройства.
 *
 * Кто кому первым шлёт приглашение, решается не переговорами, а
 * правилом: приглашает тот, чей номер меньше. Без такого правила
 * двое, нажавшие «взять» одновременно, шлют приглашения друг другу
 * и оба отвергают чужое — звонок не соединяется, и понять почему
 * невозможно.
 */

type Peer = {
  connection: RTCPeerConnection
  stream: MediaStream | null
}

type State = {
  /** Текущий звонок или ничего */
  call: Call | null
  /** Кто звонит — для входящего */
  callerName: string
  chatTitle: string
  /** Мой звук и картинка */
  local: MediaStream | null
  /** Звук и картинка собеседников */
  remote: Record<string, MediaStream>
  muted: boolean
  cameraOff: boolean
  /** Соединяемся, говорим или всё кончилось */
  phase: 'idle' | 'ringing-in' | 'ringing-out' | 'connecting' | 'talking' | 'failed'
  error: string | null

  start: (chatId: string, kind: 'AUDIO' | 'VIDEO') => Promise<void>
  accept: () => Promise<void>
  hangUp: () => Promise<void>
  toggleMute: () => void
  toggleCamera: () => void

  onIncoming: (call: Call, callerName: string, chatTitle: string) => void
  onUpdated: (call: Call) => void
  onEnded: (callId: string) => void
  onSignal: (callId: string, fromUserId: string, payload: unknown) => Promise<void>
}

/** Живут вне хранилища: это не данные для показа, а работающие объекты. */
const peers = new Map<string, Peer>()
let ringer: { stop: () => void } | null = null
let iceServers: RTCIceServer[] = []
let myId = ''

/** Кто я — узнаём один раз при первом звонке. */
export function setCallIdentity(userId: string): void {
  myId = userId
}

const stopRinging = () => {
  ringer?.stop()
  ringer = null
}

function closePeers(): void {
  for (const peer of peers.values()) {
    peer.connection.onicecandidate = null
    peer.connection.ontrack = null
    peer.connection.onconnectionstatechange = null
    peer.connection.close()
  }
  peers.clear()
}

export const useCall = create<State>((set, get) => ({
  call: null,
  callerName: '',
  chatTitle: '',
  local: null,
  remote: {},
  muted: false,
  cameraOff: false,
  phase: 'idle',
  error: null,

  /* Начать звонок ---------------------------------------------------------- */

  start: async (chatId, kind) => {
    if (get().phase !== 'idle') return

    set({ phase: 'connecting', error: null, callerName: '', chatTitle: '' })

    try {
      forRecording()
      const local = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: kind === 'VIDEO' ? { facingMode: 'user' } : false,
      })

      const { iceServers: servers } = await api<{ iceServers: RTCIceServer[] }>('/api/calls/ice')
      iceServers = servers

      const { call } = await api<{ call: Call }>('/api/calls', {
        method: 'POST',
        body: { chatId, kind },
      })

      ringer = startRinging({ outgoing: true })
      set({ call, local, phase: 'ringing-out', cameraOff: false, muted: false })
    } catch (error) {
      set({
        phase: 'failed',
        error:
          error instanceof DOMException
            ? 'Нет доступа к микрофону. Разреши его в настройках браузера.'
            : error instanceof Error
              ? error.message
              : 'Не получилось позвонить',
      })
      setTimeout(() => get().hangUp(), 2500)
    }
  },

  /* Взять трубку ----------------------------------------------------------- */

  accept: async () => {
    const call = get().call
    if (!call) return

    stopRinging()
    set({ phase: 'connecting' })

    try {
      forRecording()
      const local = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: call.kind === 'VIDEO' ? { facingMode: 'user' } : false,
      })

      const { iceServers: servers } = await api<{ iceServers: RTCIceServer[] }>('/api/calls/ice')
      iceServers = servers

      set({ local })
      await api(`/api/calls/${call.id}/accept`, { method: 'POST' })
    } catch (error) {
      set({
        phase: 'failed',
        error:
          error instanceof DOMException
            ? 'Нет доступа к микрофону'
            : 'Не получилось взять трубку',
      })
      setTimeout(() => get().hangUp(), 2500)
    }
  },

  /* Положить трубку -------------------------------------------------------- */

  hangUp: async () => {
    const call = get().call
    stopRinging()
    closePeers()

    get().local?.getTracks().forEach((track) => track.stop())
    releaseRecording()

    set({
      call: null,
      local: null,
      remote: {},
      phase: 'idle',
      error: null,
      muted: false,
      cameraOff: false,
    })

    if (call) {
      await api(`/api/calls/${call.id}/leave`, { method: 'POST' }).catch(() => {})
    }
  },

  toggleMute: () => {
    const local = get().local
    if (!local) return
    const next = !get().muted
    local.getAudioTracks().forEach((track) => (track.enabled = !next))
    set({ muted: next })
  },

  toggleCamera: () => {
    const local = get().local
    if (!local) return
    const next = !get().cameraOff
    local.getVideoTracks().forEach((track) => (track.enabled = !next))
    set({ cameraOff: next })
  },

  /* Что приходит с сервера -------------------------------------------------- */

  onIncoming: (call, callerName, chatTitle) => {
    // Уже говорим — второй звонок не перебивает первый
    if (get().phase !== 'idle') return

    ringer = startRinging()
    set({ call, callerName, chatTitle, phase: 'ringing-in', error: null })
  },

  onUpdated: (call) => {
    const current = get().call
    if (!current || current.id !== call.id) return

    set({ call })

    // Собеседник взял трубку: пора соединяться
    if (call.status === 'ACTIVE') {
      stopRinging()
      if (get().phase === 'ringing-out' || get().phase === 'connecting') {
        set({ phase: 'connecting' })
      }

      for (const participant of call.participants) {
        if (participant.userId === myId || !participant.joined || participant.left) continue
        // Приглашение шлёт тот, чей номер меньше: иначе оба пошлют
        // навстречу и оба откажутся
        if (myId < participant.userId) void invite(participant.userId, set, get)
      }
    }
  },

  onEnded: (callId) => {
    if (get().call?.id !== callId) return
    void get().hangUp()
  },

  onSignal: async (callId, fromUserId, payload) => {
    const call = get().call
    if (!call || call.id !== callId) return

    const message = payload as {
      kind: 'offer' | 'answer' | 'candidate'
      sdp?: RTCSessionDescriptionInit
      candidate?: RTCIceCandidateInit
    }

    let peer = peers.get(fromUserId)

    if (message.kind === 'offer') {
      if (!peer) peer = createPeer(fromUserId, set, get)
      await peer.connection.setRemoteDescription(message.sdp!)

      const local = get().local
      if (local) {
        for (const track of local.getTracks()) {
          // Дорожки могли уже быть добавлены при встречном приглашении
          const has = peer.connection.getSenders().some((sender) => sender.track === track)
          if (!has) peer.connection.addTrack(track, local)
        }
      }

      const answer = await peer.connection.createAnswer()
      await peer.connection.setLocalDescription(answer)
      sendCallSignal(callId, fromUserId, { kind: 'answer', sdp: answer })
      return
    }

    if (!peer) return

    if (message.kind === 'answer') {
      await peer.connection.setRemoteDescription(message.sdp!)
      return
    }

    if (message.kind === 'candidate' && message.candidate) {
      // Адрес мог прийти раньше описания: тогда браузер сам его отложит
      await peer.connection.addIceCandidate(message.candidate).catch(() => {})
    }
  },
}))

/* Соединение с одним собеседником ------------------------------------------ */

type Setter = (partial: Partial<State>) => void
type Getter = () => State

function createPeer(userId: string, set: Setter, get: Getter): Peer {
  const connection = new RTCPeerConnection({ iceServers })
  const peer: Peer = { connection, stream: null }
  peers.set(userId, peer)

  connection.onicecandidate = (event) => {
    const call = get().call
    if (event.candidate && call) {
      sendCallSignal(call.id, userId, { kind: 'candidate', candidate: event.candidate.toJSON() })
    }
  }

  connection.ontrack = (event) => {
    const stream = event.streams[0]
    if (!stream) return
    peer.stream = stream
    set({ remote: { ...get().remote, [userId]: stream } })
  }

  connection.onconnectionstatechange = () => {
    const state = connection.connectionState

    if (state === 'connected') set({ phase: 'talking', error: null })

    /**
     * Разрыв — не всегда конец разговора: в метро связь пропадает
     * и возвращается. Поэтому «failed» — это конец, а «disconnected»
     * — повод подождать.
     */
    if (state === 'failed') {
      set({
        phase: 'failed',
        error: 'Связь не установилась. Бывает на строгих рабочих сетях.',
      })
    }
  }

  return peer
}

async function invite(userId: string, set: Setter, get: Getter): Promise<void> {
  const call = get().call
  const local = get().local
  if (!call || !local) return

  const peer = peers.get(userId) ?? createPeer(userId, set, get)

  for (const track of local.getTracks()) {
    const has = peer.connection.getSenders().some((sender) => sender.track === track)
    if (!has) peer.connection.addTrack(track, local)
  }

  const offer = await peer.connection.createOffer()
  await peer.connection.setLocalDescription(offer)
  sendCallSignal(call.id, userId, { kind: 'offer', sdp: offer })
}
